// client/src/components/plataforma/siteInstitucional/NavegacaoFormPage.js
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useNavItems } from '../../../hooks/useNavItems';
import { Save, Compass, Loader2 } from 'lucide-react';
import PageHeader from '../../layout/PageHeader';
import FabSaveButton from '../../common/FabSaveButton';
import { useHomePersonalizadaGate, HomePersonalizadaUpsellBanner } from './useHomePersonalizadaGate';

const NavegacaoFormPage = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEditing = !!id;
    const { hasHomePersonalizada } = useHomePersonalizadaGate();
    const { createNavItem, updateNavItem, getNavItem, catalog, loading: hookLoading } = useNavItems();

    const [formData, setFormData] = useState({
        type: 'internal',
        internalKey: '',
        label: '',
        url: '',
        openInNewTab: false,
        active: true,
        order: 0
    });
    const [isLoading, setIsLoading] = useState(false);

    const fetchItemData = useCallback(async () => {
        if (!isEditing) return;
        setIsLoading(true);
        try {
            const response = await getNavItem(id);
            setFormData({
                type: response.data.type,
                internalKey: response.data.internalKey || '',
                label: response.data.label || '',
                url: response.data.url || '',
                openInNewTab: !!response.data.openInNewTab,
                active: response.data.active,
                order: response.data.order || 0
            });
        } catch (error) {
            // useNavItems já lida com o setAlert
        } finally {
            setIsLoading(false);
        }
    }, [id, isEditing, getNavItem]);

    useEffect(() => {
        fetchItemData();
    }, [fetchItemData]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTypeChange = (newType) => {
        setFormData(prev => ({ ...prev, type: newType, internalKey: '', url: '', label: newType === prev.type ? prev.label : '' }));
    };

    const handleInternalKeyChange = (e) => {
        const key = e.target.value;
        const catalogItem = catalog.find(c => c.key === key);
        setFormData(prev => ({
            ...prev,
            internalKey: key,
            label: prev.label || catalogItem?.label || ''
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!hasHomePersonalizada) return;
        setIsLoading(true);
        try {
            if (isEditing) {
                await updateNavItem(id, formData);
            } else {
                await createNavItem(formData);
            }
            navigate('/site-institucional/navegacao');
        } catch (error) {
            // useNavItems já lida com o setAlert
        } finally {
            setIsLoading(false);
        }
    };

    const totalLoading = hookLoading || isLoading;

    return (
        <div className="page-management-container">
            <form onSubmit={handleSubmit}>
                <PageHeader
                    title={isEditing ? 'Editar Item de Navegação' : 'Novo Item de Navegação'}
                    icon={<Compass className="icon-title" />}
                    navBack={{ to: '/site-institucional/navegacao', text: 'Voltar' }}
                />

                {!hasHomePersonalizada && <HomePersonalizadaUpsellBanner />}

                <div className="content-panel mt-6">
                    <fieldset disabled={!hasHomePersonalizada} style={{ border: 'none', padding: 0, margin: 0 }}>
                    <div className="form-grid-container grid-cols-1 md:grid-cols-2">
                        <div className="form-group md:col-span-2">
                            <label>Tipo*</label>
                            <div className="flex gap-4 mt-1">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="type"
                                        checked={formData.type === 'internal'}
                                        onChange={() => handleTypeChange('internal')}
                                    />
                                    Página Interna
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="type"
                                        checked={formData.type === 'external'}
                                        onChange={() => handleTypeChange('external')}
                                    />
                                    Link Externo
                                </label>
                            </div>
                        </div>

                        {formData.type === 'internal' ? (
                            <div className="form-group md:col-span-2">
                                <label>Página*</label>
                                <select
                                    name="internalKey"
                                    value={formData.internalKey}
                                    onChange={handleInternalKeyChange}
                                    className="form-input"
                                    required
                                >
                                    <option value="" disabled>Selecione uma página</option>
                                    {catalog.map(item => (
                                        <option key={item.key} value={item.key}>{item.label}</option>
                                    ))}
                                </select>
                            </div>
                        ) : (
                            <div className="form-group md:col-span-2">
                                <label>URL*</label>
                                <input
                                    type="url"
                                    name="url"
                                    value={formData.url}
                                    onChange={handleChange}
                                    className="form-input"
                                    placeholder="https://..."
                                    required
                                />
                            </div>
                        )}

                        <div className="form-group">
                            <label>Rótulo (texto exibido)*</label>
                            <input
                                type="text"
                                name="label"
                                value={formData.label}
                                onChange={handleChange}
                                className="form-input"
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label>Ordem de Exibição</label>
                            <input
                                type="number"
                                name="order"
                                value={formData.order}
                                onChange={handleChange}
                                className="form-input"
                            />
                        </div>

                        {formData.type === 'external' && (
                            <div className="form-group">
                                <label className="flex items-center gap-2 cursor-pointer mt-6">
                                    <input
                                        type="checkbox"
                                        name="openInNewTab"
                                        checked={formData.openInNewTab}
                                        onChange={handleChange}
                                    />
                                    Abrir em nova aba
                                </label>
                            </div>
                        )}

                        <div className="form-group">
                            <label className="flex items-center gap-2 cursor-pointer mt-6">
                                <input
                                    type="checkbox"
                                    name="active"
                                    checked={formData.active}
                                    onChange={handleChange}
                                />
                                Ativo (Visível no site)
                            </label>
                        </div>
                    </div>
                    </fieldset>
                </div>

                <FabSaveButton loading={totalLoading} label="Salvar" disabled={!hasHomePersonalizada} />
            </form>
        </div>
    );
};

export default NavegacaoFormPage;
